const HASH_PREFIX = 'pbkdf2-sha256';
const HASH_ITERATIONS = 120_000;
const MIN_HASH_ITERATIONS = 100_000;
const MAX_HASH_ITERATIONS = 500_000;
const HASH_BYTES = 32;
const SALT_BYTES = 16;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) {
    throw new Error('Invalid base64');
  }
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

interface ParsedCredential {
  iterations: number;
  salt: Uint8Array;
  expected: Uint8Array;
}

function parseCredential(value: unknown): ParsedCredential | null {
  if (typeof value !== 'string') return null;
  const parts = value.split('$');
  if (parts.length !== 4) return null;
  const [prefix, iterationsText, saltText, expectedText] = parts;
  if (prefix !== HASH_PREFIX || !/^\d+$/.test(iterationsText)) return null;
  const iterations = Number(iterationsText);
  if (
    !Number.isSafeInteger(iterations) ||
    iterations < MIN_HASH_ITERATIONS ||
    iterations > MAX_HASH_ITERATIONS
  ) return null;

  try {
    const salt = base64ToBytes(saltText);
    const expected = base64ToBytes(expectedText);
    if (salt.length !== SALT_BYTES || expected.length !== HASH_BYTES) return null;
    return { iterations, salt, expected };
  } catch {
    return null;
  }
}

async function derivePinHash(pin: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await globalThis.crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    key,
    HASH_BYTES * 8,
  );
  return new Uint8Array(bits);
}

export function isPresentationPinHash(value: unknown): value is string {
  return parseCredential(value) !== null;
}

export async function hashPresentationPin(pin: string, salt?: Uint8Array): Promise<string> {
  const resolvedSalt = salt ?? globalThis.crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  if (resolvedSalt.length !== SALT_BYTES) throw new Error('Presentation PIN salt must be 16 bytes');
  const hash = await derivePinHash(pin, resolvedSalt, HASH_ITERATIONS);
  return `${HASH_PREFIX}$${HASH_ITERATIONS}$${bytesToBase64(resolvedSalt)}$${bytesToBase64(hash)}`;
}

export async function verifyPresentationPin(pin: string, credential: string): Promise<boolean> {
  const parsed = parseCredential(credential);
  if (!parsed) return false;

  try {
    const actual = await derivePinHash(pin, parsed.salt, parsed.iterations);
    let difference = 0;
    for (let index = 0; index < actual.length; index += 1) {
      difference |= actual[index] ^ parsed.expected[index];
    }
    return difference === 0;
  } catch {
    return false;
  }
}
